import React, { useState, useEffect } from 'react';
import { creditNoteService } from '../../../api/apiService';
import '../../../App.css';

/**
 * List all credit notes created. Export PDF and XRechnung XML (German e-invoice).
 */
function CreditNotesList() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // 'delete-{id}' | 'recreate-{id}'

  useEffect(() => {
  const load = async () => {
        setLoading(true);
        setError(null);
        try {
          const res = await creditNoteService.getCreditNotes({ limit: 200 });
          const data = res.data;
          setCreditNotes(data?.creditNotes || []);
        } catch (err) {
          console.error('Error loading credit notes:', err);
          setError(err.message || 'Failed to load credit notes');
          setCreditNotes([]);
        } finally {
          setLoading(false);
        }
      };
    load();
  }, []);

  const reload = () => {
    setLoading(true);
    setError(null);
    creditNoteService.getCreditNotes({ limit: 200 })
      .then((res) => setCreditNotes(res.data?.creditNotes || []))
      .catch((err) => {
        setError(err.message || 'Failed to load credit notes');
        setCreditNotes([]);
      })
      .finally(() => setLoading(false));
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return String(d);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatTime = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return String(d);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMoney = (n) => {
    if (n == null || n === '') return '—';
    const num = typeof n === 'number' ? n : parseFloat(n);
    return isNaN(num) ? String(n) : num.toFixed(2);
  };

  /** Show "—" when agency name is missing or not meaningful (e.g. single letter "s") */
  const displayAgency = (name) => {
    const s = name != null ? String(name).trim() : '';
    if (s.length <= 1) return '—';
    return s;
  };

  const handleDownloadPdf = async (cn) => {
    const key = `pdf-${cn.id}`;
    setDownloading((p) => ({ ...p, [key]: true }));
    try {
      const res = await creditNoteService.getCreditNotePdf(cn.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschrift_${cn.credit_note_number || cn.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloading((p) => ({ ...p, [key]: false }));
    }
  };

  const handleDownloadXml = async (cn) => {
    const key = `xml-${cn.id}`;
    setDownloading((p) => ({ ...p, [key]: true }));
    try {
      const res = await creditNoteService.getCreditNoteXml(cn.id);
      const blob = new Blob([res.data], { type: 'application/xml; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschrift_${cn.credit_note_number || cn.id}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('XML download failed:', err);
    } finally {
      setDownloading((p) => ({ ...p, [key]: false }));
    }
  };

  const handleDelete = async (cn) => {
    if (!window.confirm(`Delete credit note ${cn.credit_note_number || cn.id}? Bookings will be available again for creating a new credit note.`)) return;
    setActionLoading(`delete-${cn.id}`);
    setError(null);
    try {
      await creditNoteService.deleteCreditNote(cn.id);
      reload();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRecreate = async (cn) => {
    if (!window.confirm(`Recreate credit note ${cn.credit_note_number || cn.id}? It will be deleted and new one(s) created from the same bookings (correct agency/AP).`)) return;
    setActionLoading(`recreate-${cn.id}`);
    setError(null);
    try {
      await creditNoteService.recreateCreditNote(cn.id);
      reload();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to recreate');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Credit notes</h1>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Credit notes</h1>
        <p style={{ color: '#666', marginBottom: 16 }}>
          All credit notes created. From here you can view details and export later.
        </p>
        {error && (
          <div style={{ padding: 12, marginBottom: 16, background: '#f8d7da', color: '#721c24', borderRadius: 6 }}>
            {error}
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                <th style={{ padding: '10px 8px' }}>Number</th>
                <th style={{ padding: '10px 8px' }}>Type</th>
       
                <th style={{ padding: '10px 8px' }}>Working Date's</th>
                <th style={{ padding: '10px 8px' }}>Created</th>
                <th style={{ padding: '10px 8px' }}>Created by</th>
                <th style={{ padding: '10px 8px' }}>Talent</th>
                <th style={{ padding: '10px 8px' }}>Agency</th>
                <th style={{ padding: '10px 8px' }}>AP %</th>
                <th style={{ padding: '10px 8px' }}>Net</th>
                <th style={{ padding: '10px 8px' }}>VAT</th>
                <th style={{ padding: '10px 8px' }} className="credit-notes-col-gross">Gross</th>
                <th style={{ padding: '10px 8px' }}>Export</th>
                <th style={{ padding: '10px 8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {creditNotes.length === 0 ? (
                <tr>
                  <td colSpan={14} style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                    No credit notes yet. Create them from the confirmed bookings list.
                  </td>
                </tr>
              ) : (
                creditNotes.map((cn) => (
                  <tr key={cn.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 8 }}>{cn.credit_note_number || cn.id}</td>
                    <td style={{ padding: 8 }}>{cn.invoice_type || '—'}</td>
               
                    <td style={{ padding: 8 }}>
                      {cn.booking_dates ? (
                        <div className="credit-notes-booking-dates">
                          {cn.booking_dates.split(/\s*,\s*|\n/).filter(Boolean).map((date, i) => (
                            <div key={i}>{date.trim()}</div>
                          ))}
                        </div>
                      ) : formatDate(cn.invoice_date)}
                    </td>
                    <td style={{ padding: 8 }}>
                      {cn.created_at ? (
                        <div className="credit-notes-booking-dates">
                          <div>{formatDate(cn.created_at)}</div>
                          <div>{formatTime(cn.created_at)}</div>
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{ padding: 8 }}>{cn.created_by || '—'}</td>
                    <td style={{ padding: 8 }}>{cn.talent_name || '—'}</td>
                    <td style={{ padding: 8 }}>{displayAgency(cn.agency_name)}</td>
                    <td style={{ padding: 8 }}>{cn.agency_percentage != null ? `${Number(cn.agency_percentage)}%` : '—'}</td>
                    <td style={{ padding: 8 }}>{formatMoney(cn.net_amount)}</td>
                    <td style={{ padding: 8 }}>{formatMoney(cn.vat_amount)}</td>
                    <td style={{ padding: 8 }} className="credit-notes-col-gross">{formatMoney(cn.gross_amount)}</td>
                    <td style={{ padding: 8 }}>
                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(cn)}
                        disabled={downloading?.[`pdf-${cn.id}`]}
                        className="button talent-cn-export-btn"
                      >
                        {downloading?.[`pdf-${cn.id}`] ? '…' : 'PDF'}
                      </button>
                    </td>
                    <td style={{ padding: 8 }}>
                      <button
                        type="button"
                        onClick={() => handleRecreate(cn)}
                        disabled={!!actionLoading}
                        title="Delete and create new from same bookings (fix agency/AP)"
                        className="button talent-cn-export-btn"
                      >
                        {actionLoading === `recreate-${cn.id}` ? '…' : 'Recreate'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(cn)}
                        disabled={!!actionLoading}
                        title="Delete credit note; bookings can be used again"
                        className="button button-red talent-cn-export-btn"
                      >
                        {actionLoading === `delete-${cn.id}` ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CreditNotesList;
